import axios from "axios";

/**
 * Website chatbots: per-product widget configuration plus the staff-side
 * actions on a chat session (assign / resolve / reopen / create ticket).
 *
 * Backend app: wsgi/teson/chatbot, mounted at the API root. The inbox itself
 * (conversation list, messages, send, mark-read) goes through
 * `api/communication.js` like every other channel, with refs `ch:<uuid>`.
 */

const BASE = "/chatbot";

export const rowsOf = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

const query = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

// -- chatbot configuration ---------------------------------------------

export const loadChatbots = (params) =>
  axios.get(`${BASE}/chatbots/${query({ page_size: 500, ...params })}`);

export const loadChatbot = (id) => axios.get(`${BASE}/chatbots/${id}/`);
export const createChatbot = (payload) => axios.post(`${BASE}/chatbots/`, payload);
export const updateChatbot = (id, payload) =>
  axios.patch(`${BASE}/chatbots/${id}/`, payload);
export const deleteChatbot = (id) => axios.delete(`${BASE}/chatbots/${id}/`);
export const regenerateChatbotKey = (id) =>
  axios.post(`${BASE}/chatbots/${id}/regenerate-key/`);

// -- session actions ---------------------------------------------------

/** Accepts either a bare session uuid or an inbox ref (`ch:<uuid>`). */
export const sessionIdOf = (refOrId) => String(refOrId || "").replace(/^ch:/, "");

export const assignSession = (refOrId, employeeId) =>
  axios.post(`${BASE}/sessions/${sessionIdOf(refOrId)}/assign/`, {
    employee_id: employeeId || null,
  });
export const resolveSession = (refOrId) =>
  axios.post(`${BASE}/sessions/${sessionIdOf(refOrId)}/resolve/`);
export const reopenSession = (refOrId) =>
  axios.post(`${BASE}/sessions/${sessionIdOf(refOrId)}/reopen/`);

/** -> `{ticket_id, ticket_number}` */
export const createTicketFromSession = (refOrId, payload) =>
  axios.post(`${BASE}/sessions/${sessionIdOf(refOrId)}/create-ticket/`, payload);

// -- lookups shared with the Support pages -----------------------------

export const loadEmployees = () => axios.get("/employee/employees/?page_size=1000");
export const loadTicketCategories = () =>
  axios.get("/support/ticket-categories/?page_size=100");

// The employee list serialises the user flat (`user_name`, `user_email`);
// the nested `user` shape is kept for any caller that passes a detail row.
export const employeeLabel = (employee) => {
  const name =
    employee?.user_name
    || [employee?.user?.first_name, employee?.user?.last_name].filter(Boolean).join(" ");
  return (
    name || employee?.user_email || employee?.user?.email || employee?.employee_id || "Employee"
  );
};

// -- embed -------------------------------------------------------------

export const DEFAULT_WIDGET_SCRIPT_URL = "https://teson.in/widget/chat.js";

export const embedSnippet = (chatbot) => {
  if (chatbot?.embed_code) return chatbot.embed_code;
  if (!chatbot?.public_key) return "";
  return `<script src="${DEFAULT_WIDGET_SCRIPT_URL}" data-key="${chatbot.public_key}" async></script>`;
};
